'use client'

import React from 'react'
import { Strategy } from '@/types'
import { Edit3, Code, AlertTriangle, Shield } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism'

interface StrategyLogicProps {
  strategy?: Strategy
  onEditClick?: () => void
}

const StrategyLogic: React.FC<StrategyLogicProps> = ({ strategy, onEditClick }) => {
  if (!strategy) {
    return (
      <div className="text-center py-8">
        <p className="text-white/50">加载中...</p>
      </div>
    )
  }

  const markdownComponents = {
    code({ node, inline, className, children, ...props }: any) {
      const match = /language-(\w+)/.exec(className || '')
      return !inline && match ? (
        <SyntaxHighlighter
          style={tomorrow}
          language={match[1]}
          PreTag="div"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className={className} {...props}>
          {children}
        </code>
      )
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gradient">策略逻辑说明</h2>
        <button 
          className="btn-secondary flex items-center whitespace-nowrap"
          onClick={onEditClick}
        >
          <Edit3 className="w-4 h-4 mr-2" />
          编辑逻辑
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 入场规则 */}
        <div className="lg:col-span-1">
          <div className="card border-l-4 border-l-blue-500/50">
            <div className="flex items-center mb-4">
              <Code className="w-5 h-5 text-blue-400 mr-2" />
              <h3 className="text-lg font-semibold text-white">入场规则</h3>
            </div>
            {strategy.entry_rules ? (
              <div className="prose prose-sm max-w-none prose-invert">
                <ReactMarkdown components={markdownComponents}>
                  {strategy.entry_rules}
                </ReactMarkdown>
              </div>
            ) : (
              <div className="text-center py-8">
                <Code className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <p className="text-sm text-blue-400">暂无入场规则</p>
                <button className="mt-2 text-xs text-blue-400 hover:text-blue-300 underline">
                  添加入场规则
                </button>
              </div>
            )}
          </div>
        </div>

        {/* 出场规则 */}
        <div className="lg:col-span-1">
          <div className="card border-l-4 border-l-green-500/50">
            <div className="flex items-center mb-4">
              <Code className="w-5 h-5 text-green-400 mr-2" />
              <h3 className="text-lg font-semibold text-white">出场规则</h3>
            </div>
            {strategy.exit_rules ? (
              <div className="prose prose-sm max-w-none prose-invert">
                <ReactMarkdown components={markdownComponents}>
                  {strategy.exit_rules}
                </ReactMarkdown>
              </div>
            ) : (
              <div className="text-center py-8">
                <Code className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <p className="text-sm text-green-400">暂无出场规则</p>
                <button className="mt-2 text-xs text-green-400 hover:text-green-300 underline">
                  添加出场规则
                </button>
              </div>
            )}
          </div>
        </div>

        {/* 风险管理 */}
        <div className="lg:col-span-1">
          <div className="card border-l-4 border-l-red-500/50">
            <div className="flex items-center mb-4">
              <Shield className="w-5 h-5 text-red-400 mr-2" />
              <h3 className="text-lg font-semibold text-white">风险管理</h3>
            </div>
            {strategy.risk_management ? (
              <div className="prose prose-sm max-w-none prose-invert">
                <ReactMarkdown components={markdownComponents}>
                  {strategy.risk_management}
                </ReactMarkdown>
              </div>
            ) : (
              <div className="text-center py-8">
                <Shield className="w-8 h-8 text-red-400 mx-auto mb-2" />
                <p className="text-sm text-red-400">暂无风险管理</p>
                <button className="mt-2 text-xs text-red-400 hover:text-red-300 underline">
                  添加风险管理
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 策略说明 */}
      {strategy.description && (
        <div className="card">
          <div className="flex items-center mb-4">
            <AlertTriangle className="w-5 h-5 text-yellow-400 mr-2" />
            <h3 className="text-lg font-semibold text-white">策略说明</h3>
          </div>
          <div className="prose prose-sm max-w-none prose-invert">
            <ReactMarkdown components={markdownComponents}>
              {strategy.description}
            </ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  )
}

export default StrategyLogic 